<template>
  <div class="preview-canvas-container">
    <div class="canvas-wrapper">
      <canvas ref="previewCanvas" class="preview-canvas"></canvas>
      <slot name="footer"></slot>
    </div>
  </div>
  
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, watch } from "vue";
import { useProjectStore } from "@/stores/project";
import { useMapStateStore } from "@/stores/mapState";
import { useEquipmentStore } from "@/stores/equipment";
import { useI18n } from "vue-i18n";
import { getPanelImageUrl } from "../utils/panelImageUtils";


export default defineComponent({
  name: "PreviewCanvasComponent",
  setup() {
    const { t } = useI18n();
    const previewCanvas = ref<HTMLCanvasElement | null>(null);
    const projectStore = useProjectStore();
    const mapStateStore = useMapStateStore();
    const equipmentStore = useEquipmentStore();

    // Cache for panel images to avoid reloading
    const panelImageCache = ref<{ [key: string]: HTMLImageElement }>({});

    const drawPanels = async () => {
      const canvas = previewCanvas.value;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const panels = projectStore.project?.drawing_data?.panels;
      if (!panels) return;

      // Load default fallback image
      let defaultPanelImage: HTMLImageElement;
      try {
        defaultPanelImage = new Image();
        defaultPanelImage.src = require("@/assets/panel.png");
        await new Promise((resolve, reject) => {
          defaultPanelImage.onload = resolve;
          defaultPanelImage.onerror = reject;
        });
      } catch (error) {
        console.error("Error loading default panel image:", error);
        return;
      }

      for (const panel of panels) {
        if (panel.points && panel.points.length === 4) {
          // Get the panel model to determine the correct image
          const panelModel = equipmentStore.panels.find((p) => p.id === panel.model_id);
          
          let panelImage = defaultPanelImage; // Fallback to default
          
          if (panelModel) {
            const imageUrl = getPanelImageUrl(panelModel);
            
            // Check if already cached
            if (panelImageCache.value[imageUrl]) {
              panelImage = panelImageCache.value[imageUrl];
            } else {
              // Try to load the color-specific image
              try {
                const colorImage = new Image();
                colorImage.src = imageUrl;
                await new Promise((resolve, reject) => {
                  colorImage.onload = resolve;
                  colorImage.onerror = reject;
                });
                panelImageCache.value[imageUrl] = colorImage;
                panelImage = colorImage;
              } catch (error) {
                console.warn(`Failed to load color-specific panel image for ${panelModel.color}, using default`);
                // Cache the default image for this URL to avoid repeated attempts
                panelImageCache.value[imageUrl] = defaultPanelImage;
              }
            }
          }

          // Draw the panel with the appropriate image
          drawSinglePanel(panel, panelImage, ctx, canvas);
        }
      }
    };

    const drawSinglePanel = (panel: any, panelImage: HTMLImageElement, ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement) => {
      // Get original canvas dimensions
      const originalWidth =
        projectStore.project?.drawing_data?.canvas_width_px || canvas.width;
      const originalHeight =
        projectStore.project?.drawing_data?.canvas_height_px || canvas.height;

      // Calculate scaling factors
      const scaleX = canvas.width / originalWidth;
      const scaleY = canvas.height / originalHeight;

      // Scale the panel points to match the current canvas size
      const scaledPoints = panel.points.map((point: any) => ({
        x: point.x * scaleX,
        y: point.y * scaleY,
      }));

      // Calculate the center point of the panel using scaled points
      const centerX =
        scaledPoints.reduce((sum: number, p: any) => sum + p.x, 0) /
        scaledPoints.length;
      const centerY =
        scaledPoints.reduce((sum: number, p: any) => sum + p.y, 0) /
        scaledPoints.length;

      // Calculate the width and height of the panel using scaled points
      const width = Math.sqrt(
        Math.pow(scaledPoints[1].x - scaledPoints[0].x, 2) +
          Math.pow(scaledPoints[1].y - scaledPoints[0].y, 2)
      );
      const height = Math.sqrt(
        Math.pow(scaledPoints[3].x - scaledPoints[0].x, 2) +
          Math.pow(scaledPoints[3].y - scaledPoints[0].y, 2)
      );

      // Get the rotation angle from panel properties (same as DrawingComponent)
      const angle = (panel.properties?.orientation || 0) * Math.PI / 180; // Convert degrees to radians

      // Save the current context state
      ctx.save();

      // Move to the center of the panel
      ctx.translate(centerX, centerY);
      ctx.rotate(angle);

      // Draw the panel image
      ctx.globalAlpha = 0.8; // Slightly transparent
      ctx.drawImage(
        panelImage,
        -width / 2,
        -height / 2,
        width,
        height
      );
      ctx.globalAlpha = 1;

      // Restore the context state
      ctx.restore();
    };

    const initCanvas = () => {
      const canvas = previewCanvas.value;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw screenshot as background if available
      if (mapStateStore.screenshot_info?.imageUrl) {
        const backgroundImage = new Image();
        backgroundImage.src = mapStateStore.screenshot_info.imageUrl;

        backgroundImage.onload = () => {
          // Set canvas dimensions to match the image
          canvas.width = backgroundImage.width;
          canvas.height = backgroundImage.height;

          // Draw the image
          ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

          // Draw panels if they exist
          if (
            projectStore.project?.drawing_data?.panels &&
            projectStore.project.drawing_data.panels.length > 0
          ) {
            drawPanels();
          }
        };

        backgroundImage.onerror = (error) => {
          console.error("Error loading screenshot as background:", error);
        };
      } else {
        // Default canvas size if no image is available
        canvas.width = 600;
        canvas.height = 400;

        // Draw placeholder text
        const style = getComputedStyle(canvas);
        ctx.fillStyle =
          style.getPropertyValue("--color-light-light-gray").trim() ||
          "#f8f9fa";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle =
          style.getPropertyValue("--color-gray").trim() || "#666666";
        ctx.font = "20px Arial";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(
          t("previewCanvas.noScreenshot"),
          canvas.width / 2,
          canvas.height / 2
        );
      }
    };

    // Watch for changes in screenshot info
    watch(
      () => mapStateStore.screenshot_info,
      () => {
        initCanvas();
      },
      { deep: true }
    );

    // Initialize canvas when component is mounted
    onMounted(() => {
      initCanvas();
    });

    return {
      previewCanvas,
    };
  },
});
</script>

<style scoped>
.preview-canvas-container {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.canvas-wrapper {
  display: inline-block;
  text-align: left;
  max-width: 100%;
}

.preview-canvas {
  max-width: 100%;
  max-height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
}
</style>
